import { IdCanal, IdConta, IdInbox } from "../common-types";
import type { CamposWameSignup } from '@/services/whatsapp-wame/wame.types';
import { GenericService } from "../generic-service";
import type { OnboardingMeta } from "@/utils/onboarding-meta";

export type InboxType = 'INSTAGRAM_META'|'MESSENGER_META'|'WHATSAPP_EVOLUTION'|'WHATSAPP_ZAPI'|'WHATSAPP_META'|'WHATSAPP_HYBRID_COEXISTENCE'|'INSTAGRAM_LOGIN'

/** Tipo de onboarding do número Meta: NEW_NUMBER registra; BUSINESS_APP pula (coexistência). */
export type OnboardingType = 'NEW_NUMBER' | 'BUSINESS_APP';

/** Payload do POST /inboxes/embedded-signup (Embedded Signup via SDK do Facebook). */
/** Conexão preferida para mensagens livres num inbox híbrido. */
export type TransportePreferido = 'META' | 'EVOLUTION';

export type EmbeddedSignupPayload = {
    code: string,
    wabaId: string,
    phoneNumberId: string,
    idConta: string,
    nome: string,
    onboardingType?: OnboardingType,
    /**
     * Quando presente, CONECTA (coexistência) uma inbox WHATSAPP_META já existente
     * (criada sem conexão) em vez de criar uma nova — connect-existing diferido a
     * partir da área do cliente.
     */
    idInbox?: string,
    /**
     * Plataforma da inbox a criar. Ignorado quando `idInbox` está presente (aí a
     * plataforma vem da inbox alvo).
     */
    plataforma?: 'WHATSAPP_META' | 'WHATSAPP_HYBRID_COEXISTENCE',
    /** Execução aberta antes do diálogo (`abrirDialogoRastreado`); o server a retoma. */
    idExecucao?: string,
} & CamposWameSignup

/**
 * Plataformas Meta que exigem áudio em audio/mp4 (.m4a, AAC-LC) — é o único
 * formato que Instagram, Messenger e Instagram Login tocam. Para esses inboxes a
 * gravação no front é forçada a audio/mp4 e o backend rejeita outro mimetype.
 * Ver docs do recurso m4a-recording.
 *
 * O WhatsApp Cloud SAIU desta lista: nele o container decide como a mensagem
 * aparece (ver `OPUS_PREFERRED_PLATFORMS`).
 *
 * WHATSAPP_HYBRID_COEXISTENCE fica de fora: o áudio sai pela metade Evolution, que
 * aceita ogg.
 */
export const M4A_REQUIRED_PLATFORMS: readonly InboxType[] = ['INSTAGRAM_META', 'MESSENGER_META', 'INSTAGRAM_LOGIN'];

export const requerAudioM4a = (plataforma?: InboxType | null): boolean =>
    !!plataforma && M4A_REQUIRED_PLATFORMS.includes(plataforma);

/**
 * Plataformas em que o áudio deve sair em OPUS para virar NOTA DE VOZ.
 *
 * No WhatsApp Cloud o container é o que decide a renderização: só `audio/ogg`
 * com OPUS vira nota de voz (bolha com onda, play e velocidade). Em `audio/mp4`
 * a mensagem chega como ANEXO de áudio, com ícone de música — visualmente igual
 * a um arquivo escolhido da galeria, e foi esse o sintoma de quando o áudio Meta
 * passou a ser gravado em m4a.
 *
 * O gravador prefere Ogg/Opus (Firefox) ou WebM/Opus (Chrome/Edge, remuxado para
 * Ogg no servidor). Onde não houver Opus (Safari), o m4a continua valendo: o
 * áudio é entregue, só não como nota de voz.
 */
export const OPUS_PREFERRED_PLATFORMS: readonly InboxType[] = ['WHATSAPP_META'];

export const preferirAudioOpus = (plataforma?: InboxType | null): boolean =>
    !!plataforma && OPUS_PREFERRED_PLATFORMS.includes(plataforma);

/** Formato que o canal pede do gravador. */
export type FormatoDeAudioCanal = 'opus' | 'm4a' | 'livre';

export const formatoDeAudioDoCanal = (plataforma?: InboxType | null): FormatoDeAudioCanal => {
    if (preferirAudioOpus(plataforma)) return 'opus';
    if (requerAudioM4a(plataforma)) return 'm4a';
    return 'livre';
};

/**
 * Inboxes oficiais Meta operadas via OAuth que NÃO são WhatsApp
 * (Instagram Login, Instagram Business/Meta e Messenger/Meta). Não devem ser
 * alvo de fluxos pensados para WhatsApp (ex.: agendamentos, campanhas), pois seu
 * identificador não é um telefone e o modelo de envio é distinto.
 */
export const META_OFICIAL_NAO_WHATSAPP_PLATFORMS: readonly InboxType[] = ['INSTAGRAM_LOGIN', 'INSTAGRAM_META', 'MESSENGER_META'];

export const isMetaOficialNaoWhatsapp = (plataforma?: InboxType | null): boolean =>
    !!plataforma && META_OFICIAL_NAO_WHATSAPP_PLATFORMS.includes(plataforma);

/**
 * Plataformas cuja reconexão é feita LENDO QR CODE: Evolution, Z-API e a
 * coexistência (onde o QR repara a metade Evolution). As oficiais Meta ficam
 * de fora — lá a reconexão é OAuth/Embedded Signup, não há QR para ler.
 */
export const QR_RECONNECT_PLATFORMS: readonly InboxType[] = ['WHATSAPP_EVOLUTION', 'WHATSAPP_ZAPI', 'WHATSAPP_HYBRID_COEXISTENCE'];

export const reconectaPorQrCode = (plataforma?: InboxType | null): boolean =>
    !!plataforma && QR_RECONNECT_PLATFORMS.includes(plataforma);

/**
 * Plataformas com LIGAÇÃO de voz: as que transportam pelo whiz-wa-go, o único
 * gateway com as rotas `/call/*`. Z-API não as tem; as oficiais Meta não têm
 * voz nenhuma (a Cloud API não faz chamada).
 *
 * Suportar a plataforma é só metade do gate — o outro eixo é
 * `inbox.ligacoesHabilitadas` (opt-in por número, por causa do risco de ban), e
 * o servidor ainda exige que o servidor Evolution esteja marcado como `meow`.
 */
export const LIGACAO_PLATFORMS: readonly InboxType[] = ['WHATSAPP_EVOLUTION', 'WHATSAPP_HYBRID_COEXISTENCE'];

export const suportaLigacao = (plataforma?: InboxType | null): boolean =>
    !!plataforma && LIGACAO_PLATFORMS.includes(plataforma);

/** Teto de tamanho de áudio (16 MB — limite do WhatsApp, o mais restritivo). */
export const AUDIO_META_MAX_BYTES = 16 * 1024 * 1024;

/**
 * Estado de conexão de um inbox. `connected` é o agregado histórico; em
 * coexistência ele segue a metade OFICIAL (com ela de pé o inbox recebe
 * mensagens mesmo com o QR caído) e as duas metades vêm em campos próprios.
 */
export type InboxConnectionStatus = {
    connected: boolean,
    metaConectado?: boolean,
    evolutionConectado?: boolean,
};

export type InboxWithStatus = ResponseInbox & {
    isConnected: boolean,
    qrcode?: string,
    dados: {nome: string, codigo: string, imagem: string|null},
    /** Coexistência: metade oficial provisionada. */
    metaConectado?: boolean,
    /** Coexistência: metade Evolution (QR) pareada e aberta. */
    evolutionConectado?: boolean,
    /**
     * O `GET /status` não respondeu — não sabemos se a conexão está de pé.
     *
     * `isConnected` colapsa esse caso em `false` (é o que impede um alerta falso de
     * "inbox caiu"), então ele sozinho não distingue "caiu" de "não deu para saber".
     * Este campo preserva a diferença, que é o que decide se a UI pode oferecer o
     * desvínculo da metade oficial para realinhar o nosso lado.
     */
    statusIndeterminado?: boolean,
};

/**
 * Inbox Meta criada por admin porém ainda NÃO conectada a uma conta Meta.
 * Vem separada do mapa `inboxes` do who-am-i (não tem credencial): serve só
 * para listar no InboxModal e oferecer "Conectar com a Meta". `plataforma`
 * identifica o fluxo de conexão (INSTAGRAM_LOGIN/INSTAGRAM_META/MESSENGER_META/WHATSAPP_META).
 */
export type UnconnectedInbox = { id: IdInbox, nome: string, plataforma: InboxType | string };

/**
 * Inbox ao qual o usuário está VINCULADO na conta atual — só `del = false`,
 * sem exigir integração configurada.
 *
 * É a lista completa do "de que instâncias eu faço parte", e existe porque
 * nenhuma das outras duas responde isso: o mapa `inboxes` do who-am-i só traz
 * quem TEM sub-registro de integração (é de lá que saem as credenciais), e
 * `inboxesNaoConectadas` só cobre plataformas Meta. Um inbox com `plataforma`
 * nula ficava fora das duas.
 *
 * Como as irmãs, não amplia acesso a nada: é rótulo (id/nome) para a UI listar.
 */
export type LinkedInbox = { id: IdInbox, nome: string, plataforma: InboxType | string | null, idConta: IdConta };

export type CreateInbox = {
    nome: string,
    plataforma?: InboxType,
    mensagemEntrada?: string,
    mensagemFim?: string,
    mensagemAusencia?: string,
    mensagemAniversario?: string,
    aniversarioAtivo?: boolean,
    ausente: false,
    mensagemRedirecionamento?: string,
    minutosRedirecionamento?: number,
    idCanalRedirecionamento?: IdCanal|null,
    comercial: boolean,
    finalizarAtendimentosDias?: number,
    timeZone?: string,
    idConta: IdConta,
    /** Só faz efeito em inbox WHATSAPP_HYBRID_COEXISTENCE. Default EVOLUTION. */
    transportePreferido?: TransportePreferido,
    dadosEvolution?: {
        idServidor: string
        permitirSincronizacaoFoto?: boolean
    }

    phoneNumberId?: string
    metaWabaId   ?: string
}

export type UpdateInbox = Partial<CreateInbox> & { id: IdInbox }

/** Canais de conexão de um inbox. Híbrido tem DOIS: `EVOLUTION` (QR) e `META` (oficial). */
export type CanalDeConexao = 'EVOLUTION' | 'ZAPI' | 'META' | 'INSTAGRAM_META' | 'INSTAGRAM_LOGIN';

/**
 * Uma queda em curso, por canal.
 *
 * `desde`/`motivo` vêm do HISTÓRICO e podem ser nulos: quem diz que o canal está
 * caído é o status dele, e o histórico só responde "desde quando". Canal caído
 * antes de a feature existir aparece aqui sem data — a tela mostra `—`.
 */
export type DesconexaoDeCanal = {
    canal: CanalDeConexao,
    desde: string | null,
    motivo: string | null,
};

export type EventoDeConexao = {
    id: string,
    canal: CanalDeConexao,
    evento: 'CONECTOU' | 'DESCONECTOU' | 'CANAL_REMOVIDO',
    origem: 'WEBHOOK' | 'RECONCILIACAO' | 'ACAO_ADMIN',
    motivo: string,
    dataEvento: string,
};

export type PaginaHistoricoConexao = {
    itens: EventoDeConexao[],
    /** `dataEvento` do último item; passe em `antesDe` para a próxima página. */
    proximoCursor: string | null,
};

export type ResponseInbox = {
    id                       : IdInbox;
    nome                     : string,
    /**
     * Data conhecida mais ANTIGA entre os canais caídos. `null` quando nada está
     * caído — ou quando está, mas sem histórico (inbox que já estava fora do ar
     * antes de a feature existir).
     */
    desconectadoDesde       ?: string | null,
    desconexoes             ?: DesconexaoDeCanal[],
    /**
     * Ligações de voz habilitadas nesta inbox.
     *
     * Eixo diferente de `EvolutionServidor.meow`: aquele diz se o servidor
     * CONSEGUE (tem as rotas /call/*), este diz se a inbox QUER. O backend
     * recusa habilitar quando o servidor não é meow.
     */
    ligacoesHabilitadas     ?: boolean,
    plataforma              ?: InboxType,
    mensagemEntrada         ?: string,
    mensagemFim             ?: string,
    mensagemAusencia        ?: string,
    mensagemAniversario     ?: string,
    aniversarioAtivo        ?: boolean,
    ausente                  : false,
    mensagemRedirecionamento?: string,
    minutosRedirecionamento ?: number,
    idCanalRedirecionamento ?: IdCanal|null,
    comercial                : boolean,
    usarFilaEnvio            : boolean,
    idConta                  : IdConta,

    finalizarAtendimentosDias?: number,
    timeZone                 ?: string,

    armazenamento   ?: number,

    /**
     * Inbox elegível à conversão para WHATSAPP_HYBRID_COEXISTENCE, nas duas
     * direções: WHATSAPP_META + BUSINESS_APP (falta a metade Evolution) ou
     * WHATSAPP_EVOLUTION conectado (falta a metade oficial). Só vem nas
     * listagens (`getAll`, `getByIdConta`).
     */
    convertivelParaHibrido?: boolean,

    /**
     * Plataformas de destino aceitas por POST /inboxes/:id/converter — as duas
     * outras plataformas WhatsApp quando o inbox é uma das três conversíveis,
     * `[]` para qualquer outra. Só vem nas listagens (`getAll`, `getByIdConta`).
     */
    conversoesDisponiveis?: InboxType[],

    /**
     * Conexão preferida para mensagens livres num inbox híbrido. Default
     * EVOLUTION — a preferência META só vale dentro da janela de 24h, porque
     * fora dela a Cloud API só aceita template.
     */
    transportePreferido?: TransportePreferido,

    // Registro no gateway (correlação pid→ambiente).
    gatewayRegistrado?: boolean,
    gatewayPid       ?: string | null,
    gatewayInboxId   ?: string | null,
    gatewayAmbienteId?: number | null,

    idInstanciaBotIa?: string
    idAgenteBotIa   ?: string

    /**
     * Exclusão lógica. Vem nas listagens que devolvem a linha crua do inbox —
     * `GET /contas` inclui os inboxes da conta SEM filtrar por este campo, então
     * quem consome precisa decidir se mostra vínculo morto.
     */
    del             ?: boolean
    
    dadosEvolution?: {
        idServidor: string
    },
    conta?: {
      id: string,
      nome: string
    },
    whatsappMeta?: {
        id                     : string | null,
        idInbox                : string | null,
        phoneNumberId          : string | null,
        wabaId                 : string | null,
        verifiedName          ?: string | null,
        codeVerificationStatus?: string | null,
        displayPhoneNumber    ?: string | null,
        qualityRating         ?: string | null,
        platformType          ?: string | null,
        throughputLevel       ?: string | null,
        webhookApplication    ?: string | null,
        onboardingType        ?: string | null,
        registrationStatus    ?: string | null,
    } | null,
    /**
     * COMO a metade oficial foi onboardada, derivado pelo servidor — a
     * plataforma `WHATSAPP_META` sozinha não distingue Cloud API pura de
     * coexistência. `null` nas outras plataformas. Ver `@/utils/onboarding-meta`.
     */
    onboardingMeta?: OnboardingMeta | null,
    /**
     * F-08: o inbox tem token Meta/Instagram próprio guardado. O valor nunca vem;
     * ver `revelarTokenMeta` (ação auditada).
     */
    tokenMetaDefinido?: boolean,
    whatsappEvolution?: {
      status: string,
      codigo?: string,
      permitirSincronizacaoFoto?: boolean,
      evolutionServidor: {
        id: string,
        baseUrl: string
      }
    } | null,
    // F-08: credenciais (token/clientToken Z-API, apiKey do servidor, tokens
    // Meta) não vêm mais em nenhuma resposta de inbox.
    WhatsappZapi?: {
      idInstanciaZapi: string,
      status: string
    } | null,
    instagramMeta?: {
      id                 : string,
      idInbox            : string,
      pageId             : string,
      pageName           : string,
      igBusinessAccountId: string,
      status             : 'PENDING' | 'CONNECTED' | 'EXPIRED' | 'REVOKED',
      dataCriacao       ?: string,
    } | null,
    instagramLogin?: {
      id        : string,
      idInbox   : string,
      igUserId  : string,
      igUsername: string | null,
      status    : 'PENDING' | 'CONNECTED' | 'EXPIRED' | 'REVOKED',
      dataCriacao?: string,
    } | null
}

/** Ambiente do gateway (espelha o recurso `ambientes` do gateway). */
export type GatewayAmbiente = { id: number, nome: string, url: string, del: boolean };

/** Resultado do vínculo de uma inbox ao gateway. */
export type RegisterInboxGatewayResult = {
    registrado: boolean,
    jaExistia: boolean,
    pid: string,
    idAmbiente: number,
    gatewayInboxId: string | null,
};

/**
 * Plataformas Meta elegíveis ao registro no gateway. Coexistência entra com o
 * mesmo pid (`phoneNumberId`) e o mesmo ambiente de WHATSAPP_META.
 */
export const GATEWAY_ELIGIBLE_PLATFORMS: readonly string[] = [
    'WHATSAPP_META', 'WHATSAPP_HYBRID_COEXISTENCE', 'INSTAGRAM_META', 'MESSENGER_META', 'INSTAGRAM_LOGIN',
];

export const podeRegistrarNoGateway = (plataforma?: string | null): boolean =>
    !!plataforma && GATEWAY_ELIGIBLE_PLATFORMS.includes(plataforma);

/**
 * Normaliza o valor de plataforma para a chave canônica do enum Meta
 * (INSTAGRAM_META / MESSENGER_META). O front já usa os nomes do enum; este mapper
 * permanece como defesa para eventuais valores legados (INSTAGRAM / FACEBOOK).
 */
export const normalizarPlataformaMeta = (plataforma?: string | null): string | null => {
    if (!plataforma) return null;
    if (plataforma === 'INSTAGRAM') return 'INSTAGRAM_META';
    if (plataforma === 'FACEBOOK') return 'MESSENGER_META';
    return plataforma;
};

/** Plataformas cujo inbox guarda token Meta/Instagram próprio (o que "Revelar token" mostra). */
export const PLATAFORMAS_COM_TOKEN_META: readonly string[] = [
    'WHATSAPP_META', 'WHATSAPP_HYBRID_COEXISTENCE', 'INSTAGRAM_META', 'MESSENGER_META', 'INSTAGRAM_LOGIN',
];

/** Resposta do "Revelar token" (ação auditada, com motivo obrigatório). */
export type TokenMetaRevelado = {
    credencial: string,
    fonte: 'WHATSAPP_META' | 'INSTAGRAM_META_PAGINA' | 'INSTAGRAM_LOGIN',
    credencialMascarada: string,
};

export class InboxService extends GenericService<CreateInbox, UpdateInbox, ResponseInbox> {
    constructor() { super('inboxes') }

    /**
     * F-08: revela o token Meta/Instagram do inbox. Exige permissão de admin e
     * motivo; o server grava a auditoria ANTES de devolver o valor.
     */
    async revelarTokenMeta(idInbox: IdInbox, motivo: string): Promise<TokenMetaRevelado> {
        return await this.f.post(`/${idInbox}/credenciais/meta/revelar`, { motivo }) as TokenMetaRevelado;
    }

    /** Lista os ambientes ativos do gateway (para escolher no vínculo). */
    async listarAmbientesGateway(): Promise<GatewayAmbiente[]> {
        return await this.f.get('/gateway/ambientes') as GatewayAmbiente[];
    }

    /** Mapa plataforma Meta → ambiente padrão do gateway (env do server) para pré-seleção. */
    async obterAmbientesGatewayPadrao(): Promise<Record<string, number | null>> {
        return await this.f.get('/gateway/default-ambientes') as Record<string, number | null>;
    }

    /**
     * (Re)registra o número WhatsApp Meta na Cloud API (Register API). Retry do
     * registro automático do onboarding, quando ele falhou. Só admin.
     */
    async registrarNumeroMeta(idInbox: string): Promise<{ registrationStatus: string }> {
        return await this.f.post(`/${idInbox}/register-number`, {}) as { registrationStatus: string };
    }

    /** Vincula a inbox ao gateway no ambiente informado. */
    async registrarNoGateway(idInbox: string, idAmbiente: number): Promise<RegisterInboxGatewayResult> {
        return await this.f.post(`/${idInbox}/gateway-register`, { idAmbiente }) as RegisterInboxGatewayResult;
    }

    /**
     * Liga ou desliga ligações de voz na inbox.
     *
     * O backend grava o flag e, em seguida, liga/desliga o evento `call` na
     * lista de webhooks do gateway — para inbox desligada não gerar tráfego
     * inútil. Recusa com 400 quando o servidor Evolution não é meow.
     */
    async definirLigacoes(idInbox: string, habilitado: boolean): Promise<{ id: string; ligacoesHabilitadas: boolean }> {
        return await this.f.patch(`/${idInbox}/ligacoes`, { habilitado }) as { id: string; ligacoesHabilitadas: boolean };
    }

    async conectar(idInbox: string): Promise<{ base64: string }> {
        return await this.f.get(`/${idInbox}/conectar`) as { base64: string };
    }

    async desconectar(idInbox: string): Promise<unknown> {
        return await this.f.get(`/${idInbox}/desconectar`);
    }

    async getStatusConexao(idInbox: string, falseOnFail: boolean = true): Promise<InboxConnectionStatus> {
        try {
            return await this.f.get(`/${idInbox}/status`) as InboxConnectionStatus;
        } catch (e) {
            if (!falseOnFail) throw e;
            console.warn('[getStatusConexao]', e);
            return { connected: false };
        }
    }

    /**
     * Status da conexão em três estados: `true`/`false` vindos do backend e `null`
     * quando a consulta falhou (ex.: 404 logo depois de conectar, enquanto o token
     * do usuário ainda não carrega o inbox). O `null` é o que impede tratar uma
     * falha de consulta como "inbox caiu" e disparar alerta de desconexão falso.
     */
    async getStatusConexaoOuNulo(idInbox: string): Promise<boolean | null> {
        return (await this.getStatusConexaoOuIndeterminado(idInbox))?.connected ?? null;
    }

    /**
     * Igual ao `getStatusConexaoOuNulo`, mas devolve o objeto inteiro — em
     * coexistência os campos `metaConectado`/`evolutionConectado` dizem QUAL
     * metade caiu, o que o booleano agregado não consegue expressar.
     * `null` = consulta falhou (status indeterminado).
     */
    async getStatusConexaoOuIndeterminado(idInbox: string): Promise<InboxConnectionStatus | null> {
        try {
            return await this.getStatusConexao(idInbox, false);
        } catch (e) {
            console.warn('[getStatusConexao] status indeterminado para', idInbox, e);
            return null;
        }
    }

    async getByIdConta(idConta: string): Promise<ResponseInbox[]> {
        return await this.f.get(`/conta/${idConta}`) as ResponseInbox[];
    }

    async getDadosDetalhados(idInbox: string): Promise<{nome: string, imagem: string|null, codigo: string}> {
        try {
            return await this.f.get(`/${idInbox}/dados`) as {nome: string, imagem: string|null, codigo: string};
        } catch (e) {
            console.groupCollapsed('⚠️ Expected error for disconnected inbox:', idInbox)
            console.warn(e);
            console.groupEnd();
            // toast.error((e as Error).message || `Não foi possível obter dados detalhados do inbox #${idInbox}`);
            return { nome: '', imagem: null, codigo: '' }
        }
    }

    async restaurarBotsPadrao(idInbox: IdInbox) {
      return await this.f.patch(`/${idInbox}/restaurar-bots`);
    }

    async logout(idInbox: IdInbox): Promise<unknown> {
        return await this.f.delete(`/${idInbox}/logout`);
    }

    async restart(idInbox: IdInbox): Promise<unknown> {
        return await this.f.post(`/${idInbox}/restart`);
    }

    async transferir(idInbox: IdInbox, idServidor: string): Promise<unknown> {
        return await this.f.post(`/${idInbox}/transfer`, { idServidor });
    }

    /**
     * Migra a sessão whiz-wa-go para o hive SEM novo QR (temporário; whiz-server
     * docs/specs/migrar-hive.md). Diferente do `transferir`, que recria a
     * instância vazia no destino.
     */
    async migrarParaHive(idInbox: IdInbox, idServidor: string): Promise<unknown> {
        return await this.f.post(`/${idInbox}/migrate-hive`, { idServidor });
    }

    /**
     * Linha do tempo de conexões do inbox (modal "Histórico de conexões").
     *
     * Paginação por CURSOR: `antesDe` recebe o `proximoCursor` da página
     * anterior. Offset não serve — a lista recebe linhas novas no topo e
     * repetiria/pularia registros entre páginas.
     */
    async historicoConexoes(
        idInbox: IdInbox,
        params: { limit?: number, antesDe?: string | null } = {},
    ): Promise<PaginaHistoricoConexao> {
        const busca = new URLSearchParams();
        if (params.limit) busca.set('limit', String(params.limit));
        if (params.antesDe) busca.set('antesDe', params.antesDe);
        const query = busca.toString();
        return await this.f.get(`/${idInbox}/conexao-eventos${query ? `?${query}` : ''}`) as PaginaHistoricoConexao;
    }

    /**
     * Cria um inbox WhatsApp Meta via Embedded Signup (SDK do Facebook).
     * Usado no fluxo de coexistência: envia code + asset IDs capturados no SDK
     * com onboardingType=BUSINESS_APP (backend pula o registro do número).
     */
    async embeddedSignup(payload: EmbeddedSignupPayload): Promise<ResponseInbox> {
        return await this.f.post('/embedded-signup', payload) as ResponseInbox;
    }

    /**
     * Variante do Embedded Signup para a área do cliente (token de usuário).
     * Usada no self-connect: o admin criou o inbox, o operador conecta a metade
     * oficial informando `idInbox`.
     */
    async embeddedSignupUsuario(payload: EmbeddedSignupPayload): Promise<ResponseInbox> {
        return await this.f.post('/embedded-signup/user', payload) as ResponseInbox;
    }

    /**
     * Converte um inbox WHATSAPP_META conectado por coexistência em
     * WHATSAPP_HYBRID_COEXISTENCE, acoplando uma instância Evolution ao mesmo número.
     * A conexão oficial, o vínculo no gateway e o histórico são preservados.
     */
    async converterParaCoexistencia(idInbox: IdInbox, idServidorEvolution: string): Promise<ResponseInbox> {
        return await this.f.post(`/${idInbox}/converter-coexistencia`, { idServidorEvolution }) as ResponseInbox;
    }

    /**
     * Direção inversa: acopla a metade OFICIAL a um inbox WHATSAPP_EVOLUTION já
     * pareado, passando a plataforma para WHATSAPP_HYBRID_COEXISTENCE. O payload é
     * o do Embedded Signup (o popup da Meta devolve code/wabaId/phoneNumberId).
     *
     * Recusa com 409 se o número escolhido no popup não for o mesmo pareado no QR.
     */
    async converterHibridoEvolution(
        idInbox: IdInbox,
        payload: Pick<EmbeddedSignupPayload, 'code' | 'wabaId' | 'phoneNumberId'>,
    ): Promise<ResponseInbox> {
        return await this.f.post(`/${idInbox}/converter-hibrido-evolution`, payload) as ResponseInbox;
    }

    /**
     * Mesma conversão, sem conectar agora: a plataforma vira híbrida e a metade
     * oficial fica pendente para o cliente conectar pela área do app. Não exige o
     * QR pareado — o número é conferido no momento da conexão.
     */
    async converterHibridoEvolutionDiferido(idInbox: IdInbox): Promise<ResponseInbox> {
        return await this.f.post(`/${idInbox}/converter-hibrido-evolution/diferido`, {}) as ResponseInbox;
    }

    /**
     * Conversão LIVRE de plataforma entre WHATSAPP_EVOLUTION, WHATSAPP_META e
     * WHATSAPP_HYBRID_COEXISTENCE, em qualquer direção. Preserva o inbox (e com
     * ele atendimentos, contatos, mensagens e canais).
     *
     * `idServidorEvolution` é obrigatório quando o destino exige a metade QR e o
     * inbox ainda não a tem. A metade OFICIAL faltante fica pendente: conectá-la
     * exige o popup da Meta, então quem fecha é o cliente pela área do app.
     *
     * `onboardingMeta` só vale para o destino WHATSAPP_META e escolhe o sabor da
     * conexão oficial: BUSINESS_APP (coexistência — número já ativo no app
     * WhatsApp Business, segue elegível a virar híbrido) ou NEW_NUMBER (Cloud API
     * pura, que não aceita dispositivo companheiro).
     */
    async converterPlataforma(
        idInbox: IdInbox,
        plataformaDestino: InboxType,
        idServidorEvolution?: string,
        onboardingMeta?: OnboardingType,
    ): Promise<ResponseInbox> {
        return await this.f.post(`/${idInbox}/converter`, {
            plataformaDestino,
            ...(idServidorEvolution ? { idServidorEvolution } : {}),
            ...(onboardingMeta ? { onboardingMeta } : {}),
        }) as ResponseInbox;
    }

    /**
     * Desvincula SÓ a metade oficial (Cloud API), preservando a conexão por QR.
     * A plataforma não muda: o inbox segue híbrido com metaConectado=false.
     * Exige a permissão de conta `conta.reconectar_meta`.
     */
    async desvincularConexaoMeta(idInbox: IdInbox): Promise<void> {
        await this.f.delete(`/${idInbox}/conexao-meta`);
    }

    /**
     * Variante de auto-onboarding do usuário do Embedded Signup (coexistência).
     * Autenticada pelo token de usuário: o backend vincula o próprio usuário ao
     * inbox, cria o template de boas-vindas e emite o evento INBOX_PRONTO.
     */
    async embeddedSignupUser(payload: EmbeddedSignupPayload): Promise<ResponseInbox> {
        return await this.f.post('/embedded-signup/user', payload) as ResponseInbox;
    }

    // ── DEBUG (Meta oficial): testa o fluxo de concessão sem reconectar à Meta ──

    /** DEBUG: hard-delete das permissões de usuário do inbox Meta + limpa cache. */
    async debugRemoverPermissoes(idInbox: IdInbox): Promise<{ removidas: number; usuariosAfetados: string[] }> {
        return await this.f.delete(`/${idInbox}/debug/user-permissions`) as { removidas: number; usuariosAfetados: string[] };
    }

    /** DEBUG: hard-delete dos vínculos usuário-inbox do inbox Meta + limpa cache. */
    async debugRemoverVinculos(idInbox: IdInbox): Promise<{ removidos: number; usuariosAfetados: string[] }> {
        return await this.f.delete(`/${idInbox}/debug/user-links`) as { removidos: number; usuariosAfetados: string[] };
    }

    /** DEBUG: refaz o binding usuário→inbox como o OAuth Meta e emite INBOX_PRONTO. */
    async debugRebindUsuario(idInbox: IdInbox, idUsuario: string): Promise<{ ok: true }> {
        return await this.f.post(`/${idInbox}/debug/rebind-user`, { idUsuario }) as { ok: true };
    }
}
